import { toast } from 'react-toastify'

// Types for different error categories
type ErrorType =
  | 'network'
  | 'server'
  | 'validation'
  | 'auth'
  | 'payment'
  | 'general'

interface ErrorState {
  lastNetworkErrorTime: number
  networkErrorCount: number
  isNetworkErrorActive: boolean
  lastShownErrors: Map<string, number>
}

class ErrorToastManager {
  private state: ErrorState = {
    lastNetworkErrorTime: 0,
    networkErrorCount: 0,
    isNetworkErrorActive: false,
    lastShownErrors: new Map(),
  }

  // Configuration
  private readonly NETWORK_ERROR_COOLDOWN = 10000 // 10 seconds

  private readonly GENERAL_ERROR_COOLDOWN = 3000 // 3 seconds

  private readonly MAX_NETWORK_ERRORS_BEFORE_CONSOLIDATION = 2

  private readonly ERROR_MESSAGE_TIMEOUT = 5000

  /**
   * Determines the error type based on the error details
   */
  private static categorizeError(error: any, status?: number): ErrorType {
    if (!error) return 'general'

    const message = error.message?.toLowerCase() || ''

    // Network-related errors
    if (
      message.includes('network error') ||
      message.includes('failed to fetch') ||
      message.includes('timeout') ||
      message.includes('connection') ||
      error.code === 'NETWORK_ERROR' ||
      !status // No status usually means network issue
    ) {
      return 'network'
    }

    // Status-based categorization
    if (status) {
      if (status >= 500) return 'server'
      if (status === 401 || status === 403) return 'auth'
      if (status >= 400 && status < 500) return 'validation'
    }

    // Payment-related errors
    if (
      message.includes('payment') ||
      message.includes('stripe') ||
      message.includes('card')
    ) {
      return 'payment'
    }

    return 'general'
  }

  /**
   * Gets user-friendly error message based on error type
   */
  private getFriendlyMessage(
    errorType: ErrorType,
    originalMessage?: string
  ): string {
    switch (errorType) {
      case 'network':
        if (
          this.state.networkErrorCount <=
          this.MAX_NETWORK_ERRORS_BEFORE_CONSOLIDATION
        ) {
          return 'Connection issue detected. Retrying...'
        }
        return 'You appear to be offline or having connection issues. Please check your internet connection.'

      case 'server':
        return 'Our servers are experiencing issues. Please try again in a moment.'

      case 'auth':
        return 'Your session has expired. Please refresh the page.'

      case 'payment':
        return originalMessage || 'Payment processing issue. Please try again.'

      case 'validation':
        return originalMessage || 'Please check your input and try again.'

      case 'general':
      default:
        return originalMessage || 'Something went wrong. Please try again.'
    }
  }

  /**
   * Checks if we should show an error toast based on cooldown and deduplication
   */
  private shouldShowError(errorType: ErrorType, message: string): boolean {
    const now = Date.now()
    const errorKey = `${errorType}-${message}`

    // Check for network error cooldown
    if (errorType === 'network') {
      if (this.state.isNetworkErrorActive) {
        return false // Already showing network error
      }

      if (now - this.state.lastNetworkErrorTime < this.NETWORK_ERROR_COOLDOWN) {
        this.state.networkErrorCount += 1
        return false
      }
    }

    // Check for general error deduplication
    const lastShown = this.state.lastShownErrors.get(errorKey)
    if (lastShown && now - lastShown < this.GENERAL_ERROR_COOLDOWN) {
      return false
    }

    return true
  }

  /**
   * Updates internal state after showing an error
   */
  private updateStateAfterError(errorType: ErrorType, message: string): void {
    const now = Date.now()
    const errorKey = `${errorType}-${message}`

    if (errorType === 'network') {
      this.state.lastNetworkErrorTime = now
      this.state.networkErrorCount += 1
      this.state.isNetworkErrorActive = true

      // Clear network error state after timeout
      setTimeout(() => {
        this.state.isNetworkErrorActive = false
        this.state.networkErrorCount = 0
      }, this.NETWORK_ERROR_COOLDOWN)
    }

    this.state.lastShownErrors.set(errorKey, now)

    // Clean up old entries
    setTimeout(() => {
      this.state.lastShownErrors.delete(errorKey)
    }, this.ERROR_MESSAGE_TIMEOUT)
  }

  /**
   * Main method to handle errors intelligently
   */
  handleError(error: any, status?: number, context?: string): void {
    try {
      const errorType = ErrorToastManager.categorizeError(error, status)
      const friendlyMessage = this.getFriendlyMessage(errorType, error?.message)

      if (!this.shouldShowError(errorType, friendlyMessage)) {
        console.log(`Error suppressed (${errorType}):`, error)
        return
      }

      // Show appropriate toast based on error type
      const toastOptions: any = {
        toastId: errorType === 'network' ? 'network-error' : undefined,
        autoClose: errorType === 'network' ? false : 5000,
      }

      switch (errorType) {
        case 'network':
          toast.error(friendlyMessage, {
            ...toastOptions,
            onClose: () => {
              this.state.isNetworkErrorActive = false
            },
          })
          break

        case 'server':
          toast.error(friendlyMessage, { ...toastOptions, autoClose: 8000 })
          break

        case 'auth':
          toast.warning(friendlyMessage, { ...toastOptions, autoClose: 10000 })
          break

        case 'payment':
          toast.error(friendlyMessage, toastOptions)
          break

        default:
          toast.error(friendlyMessage, toastOptions)
      }

      this.updateStateAfterError(errorType, friendlyMessage)

      // Log detailed error for debugging
      console.error(`[${errorType.toUpperCase()}] ${context || 'API Error'}:`, {
        originalError: error,
        status,
        friendlyMessage,
        timestamp: new Date().toISOString(),
      })
    } catch (err) {
      console.error('Error in ErrorToastManager:', err)
      // Fallback to simple toast
      toast.error('An unexpected error occurred')
    }
  }

  /**
   * Handle successful recovery from network issues
   */
  async handleNetworkRecovery(): Promise<void> {
    console.log(
      '🔄 handleNetworkRecovery called, isNetworkErrorActive:',
      this.state.isNetworkErrorActive
    )

    // Import networkRetryManager dynamically to avoid circular dependency
    // eslint-disable-next-line import/no-cycle
    const { networkRetryManager } = await import('./networkRetryManager')

    // Get pending requests count before retrying
    const pendingCount = networkRetryManager.getPendingRequestsCount()
    console.log('🔄 Pending requests count:', pendingCount)

    // Always check for pending requests, regardless of network error state
    if (pendingCount > 0) {
      if (this.state.isNetworkErrorActive) {
        toast.dismiss('network-error')
      }

      toast.success('Connection restored!', {
        toastId: 'network-recovery',
        autoClose: 3000,
      })

      // Start retrying failed requests
      console.log('🔄 Calling networkRetryManager.retryFailedRequests()')
      networkRetryManager.retryFailedRequests().catch((error) => {
        console.error('Error during automatic retry:', error)
      })
    } else if (this.state.isNetworkErrorActive) {
      toast.dismiss('network-error')
      toast.success('Connection restored!', {
        toastId: 'network-recovery',
        autoClose: 3000,
      })
    }

    // Reset network error state
    if (this.state.isNetworkErrorActive) {
      this.state.isNetworkErrorActive = false
      this.state.networkErrorCount = 0
    }
  }

  /**
   * Clear all error states (useful for testing or manual resets)
   */
  reset(): void {
    this.state = {
      lastNetworkErrorTime: 0,
      networkErrorCount: 0,
      isNetworkErrorActive: false,
      lastShownErrors: new Map(),
    }
    toast.dismiss()
  }

  /**
   * Get current network status
   */
  isNetworkErrorActive(): boolean {
    return this.state.isNetworkErrorActive
  }

  /**
   * Show a success message (with deduplication)
   */
  showSuccess(message: string, options?: any): void {
    const now = Date.now()
    const lastShown = this.state.lastShownErrors.get(`success-${message}`)

    if (!lastShown || now - lastShown > 2000) {
      toast.success(message, options)
      this.state.lastShownErrors.set(`success-${message}`, now)
    }
  }
}

// Create and export singleton instance
export const errorToastManager = new ErrorToastManager()

// Export the class for testing purposes
export { ErrorToastManager }
